import { Checkbox, CheckboxProps, Chip, IconButton, InputAdornment, TextField } from '@material-ui/core'
import Collapse from '@material-ui/core/Collapse'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import { makeStyles } from '@material-ui/core/styles'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CloseIcon from '@material-ui/icons/Close'
import ExpandLess from '@material-ui/icons/ExpandLess'
import ExpandMore from '@material-ui/icons/ExpandMore'
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox'
import SearchIcon from '@material-ui/icons/Search'
import * as React from 'react'
import { Selectable, Stream } from 'src/models/IDataComponent'

interface IStreamSelector {
    streams: Stream[]
    rules?: string[]
    onRulesChange: (rules: string[]) => void
}

const _parseRule = (rule: string) => {
    let ruleRegex = rule

    if (ruleRegex.startsWith('!'))
        ruleRegex = ruleRegex.substring(1)

    ruleRegex = ruleRegex.replace('.', '\\.')
    ruleRegex = ruleRegex.replace('*', '.*')
    ruleRegex = `^${ruleRegex}$`

    return new RegExp(ruleRegex)
}

const useStyles = makeStyles({
    search: { '&:after': { display: 'none' } },
})

const SelectableCheckbox = (props: CheckboxProps) => (
    <Checkbox
        disableRipple
        color='primary'
        edge='start'
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        {...props}
    />
)

const StreamSelector: React.FunctionComponent<IStreamSelector> = (props) => {

    const streamsById = React.useMemo(
        () => {
            const result: Record<string, Stream> = {}

            for (const c of props.streams) {
                result[c.name] = {
                    ...c,
                    fields: [...c.fields].sort((a, b) => a.name.localeCompare(b.name)),
                }
            }

            return result
        },
        [props.streams],
    )

    const [expanded, setExpanded] = React.useState(new Set())
    const [rules, setRules] = React.useState(new Set<string>(props.rules))

    const pruneRules = () => {
        if (!rules.size)
            return

        const entityIds: string[] = []

        for (const stream of Object.values(streamsById))
            entityIds.push(
                stream.name,
                ...stream.fields.map((field) => `${stream.name}.${field.name}`),
            )

        setRules((rules) => {
            for (const rule of rules) {
                const parsed = _parseRule(rule)

                // remove any rules that do not match any entities (streams or properties)
                if (!entityIds.some((entityIds) => parsed.test(entityIds)))
                    rules.delete(rule)
            }

            return rules
        })
    }

    React.useEffect(() => {
        pruneRules()
    }, [])

    React.useEffect(() => {
        // console.debug(rules)
        props.onRulesChange([...rules])
    }, [rules])

    const checkFieldSelectedFromRules = React.useCallback(
        (field: Selectable, streamId: string) => {
            const fieldId = `${streamId}.${field.name}`
            const matchingRules = [...rules].filter((rule) => {
                if (field.selected !== 'AUTOMATIC')
                    return _parseRule(rule).test(fieldId)

                const [streamRule] = rule.split('.', 2)
                const exclude = streamRule.startsWith('!')

                return !exclude && _parseRule(streamRule).test(streamId)
            })

            const [lastMatchingRule] = matchingRules.slice(-1)
            const selected = !!lastMatchingRule && !lastMatchingRule.startsWith('!')

            // console.debug({fieldId, rules, matchingRules, lastMatchingRule, selected})

            return selected
        },
        [rules],
    )

    const checkFieldSelectedFromStreams = React.useCallback(
        (field: Selectable, streamId: string) => {
            const selectedFieldNames = new Set(
                streamsById[streamId].fields
                    .filter(({ selected }) => selected === 'AUTOMATIC' || !rules.size && selected === 'SELECTED')
                    .map(({ name }) => name)
            )

            return selectedFieldNames.has(field.name)
        },
        [rules],
    )

    const checkStreamSelectedFromRules = React.useCallback(
        (streamId: string) => {
            const fields = streamsById[streamId].fields
            const selectedFields = fields.filter((field) => checkFieldSelectedFromRules(field, streamId))

            const matchingRules = [...rules].filter((rule) => {
                const [streamRule] = rule.split('.', 2)
                const exclude = streamRule.startsWith('!')

                return !exclude && _parseRule(streamRule).test(streamId)
            })

            const any = matchingRules.length > 0

            return {
                any: any,
                some: any && (!matchingRules.some((rule) => rule.endsWith('.*')) || selectedFields.length < fields.length),
            }
        },
        [rules],
    )

    const checkStreamSelectedFromStreams = React.useCallback(
        (streamId: string) => {
            const stream = streamsById[streamId]
            const fields = stream.fields
            const selectedFields = fields.filter((field) => checkFieldSelectedFromStreams(field, streamId))

            const any = stream.selected !== 'EXCLUDED'

            return {
                any: any,
                some: any && (fields.length === 0 || selectedFields.length < fields.length),
            }
        },
        [rules],
    )

    const toggleExpanded = (streamId: string) => () => setExpanded((prev) => {
        const next = new Set(prev)

        if (next.has(streamId)) {
            next.delete(streamId)
        } else {
            next.add(streamId)
        }

        return next
    })

    const classes = useStyles()
    const [search, setSearch] = React.useState('')
    const matches = (name: string) => name.toLowerCase().includes(search.trim().toLowerCase())
    const visibleStreams = Object.values(streamsById)
        .filter((stream) => matches(stream.name) || stream.fields.some((field) => matches(field.name)))
    // a stream that a search finds by its properties, rather than by its name, is selected by those properties alone
    const foundFields = (stream: Stream) => matches(stream.name) ? undefined : stream.fields.filter((field) => matches(field.name))

    // selects or deselects some properties of a stream, as `<stream>.*` once every property is selected
    const selectFields = (next: Set<string>, streamId: string, names: string[], selected: boolean) => {
        const fields = streamsById[streamId].fields

        if (next.has(`${streamId}.*`)) {
            next.delete(`${streamId}.*`)

            fields.forEach(({ name }) => next.add(`${streamId}.${name}`))
        }

        names.forEach((name) => selected ? next.add(`${streamId}.${name}`) : next.delete(`${streamId}.${name}`))

        if (selected)
            fields
                .filter(({ selected }) => selected === 'AUTOMATIC')
                .forEach(({ name }) => next.add(`${streamId}.${name}`))

        if (fields.every(({ name }) => next.has(`${streamId}.${name}`))) {
            fields.forEach(({ name }) => next.delete(`${streamId}.${name}`))

            next.add(`${streamId}.*`)
        }
    }

    const toggleStreamSelected = (streamId: string) => () => setRules((prev) => {
        const next = new Set(prev)
        const found = foundFields(streamsById[streamId])

        if (found) {
            selectFields(next, streamId, found.map(({ name }) => name), !found.every((field) => checkFieldSelectedFromRules(field, streamId)))

            return next
        }

        if (next.has(`${streamId}.*`)) {
            next.delete(`${streamId}.*`)
        } else {
            next.add(`${streamId}.*`)
        }

        streamsById[streamId].fields.forEach(({ name }) => next.delete(`${streamId}.${name}`))

        return next
    })

    const toggleFieldSelected = (field: Selectable, streamId: string) => () => setRules((prev) => {
        if (field.selected === 'AUTOMATIC')
            return prev

        const next = new Set(prev)

        selectFields(next, streamId, [field.name], !next.has(`${streamId}.*`) && !next.has(`${streamId}.${field.name}`))

        return next
    })

    // like a stream's own checkbox, this shows the default selection, faded, until there are rules
    const selectedCount = visibleStreams.filter((stream) => {
        const found = foundFields(stream)

        if (found)
            return found.every((field) => rules.size ? checkFieldSelectedFromRules(field, stream.name) : checkFieldSelectedFromStreams(field, stream.name))

        return rules.size ? checkStreamSelectedFromRules(stream.name).any : checkStreamSelectedFromStreams(stream.name).any
    }).length

    // a search opens the streams it finds by one of their properties, to show them
    React.useEffect(() => {
        setExpanded(new Set(search.trim() ? visibleStreams.filter((stream) => !matches(stream.name)).map((stream) => stream.name) : []))
    }, [search])

    const toggleAllSelected = () => setRules((prev) => {
        const next = new Set(prev)

        visibleStreams.forEach((stream) => {
            const found = foundFields(stream)

            if (found)
                return selectFields(next, stream.name, found.map(({ name }) => name), !rules.size || selectedCount < visibleStreams.length)

            next.delete(`${stream.name}.*`)
            stream.fields.forEach(({ name }) => next.delete(`${stream.name}.${name}`))

            if (!rules.size || selectedCount < visibleStreams.length)
                next.add(`${stream.name}.*`)
        })

        return next
    })

    return (
        <List disablePadding dense subheader={
            <ListItem component="div">
                <ListItemIcon>
                    <SelectableCheckbox
                        checked={selectedCount > 0}
                        checkedIcon={<CheckBoxIcon opacity={rules.size ? undefined : 0.5} />}
                        indeterminate={selectedCount > 0 && selectedCount < visibleStreams.length}
                        indeterminateIcon={<IndeterminateCheckBoxIcon opacity={rules.size ? undefined : 0.5} />}
                        disabled={!visibleStreams.length}
                        inputProps={{ 'aria-label': 'Select all streams' }}
                        onClick={toggleAllSelected}
                    />
                </ListItemIcon>
                <TextField
                    fullWidth
                    placeholder="Search streams and properties"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        classes: { underline: classes.search },
                        endAdornment: <InputAdornment position="end">
                            {search
                                ? <IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}><CloseIcon fontSize="small" /></IconButton>
                                : <SearchIcon fontSize="small" />}
                        </InputAdornment>,
                    }}
                />
            </ListItem>
        }>
            {!visibleStreams.length && <ListItem><ListItemText secondary="No streams or properties match" /></ListItem>}
            {visibleStreams.map((stream) => {
                const streamId = stream.name
                const labelId = `${streamId}-label`

                const selectedFromRules = checkStreamSelectedFromRules(streamId)
                const selectedFromStreams = checkStreamSelectedFromStreams(streamId)
                const selectedFromStreamsOnly = !selectedFromRules.any && selectedFromStreams.any

                return (
                    <React.Fragment key={streamId}>
                        <ListItem
                            button
                            onClick={toggleExpanded(streamId)}
                            aria-labelledby={labelId}
                        >
                            <ListItemIcon>
                                <SelectableCheckbox
                                    checked={rules.size ? selectedFromRules.any : selectedFromStreams.any}
                                    checkedIcon={<CheckBoxIcon opacity={selectedFromStreamsOnly ? 0.5 : undefined} />}
                                    indeterminate={rules.size ? selectedFromRules.some : selectedFromStreams.some}
                                    indeterminateIcon={<IndeterminateCheckBoxIcon opacity={selectedFromStreamsOnly ? 0.5 : undefined} />}
                                    onClick={(e) => {
                                        toggleStreamSelected(streamId)()
                                        e.stopPropagation()
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText primary={<code id={labelId}>{streamId}</code>} />
                            {!!stream.fields.length && (expanded.has(streamId)
                                ? <ExpandLess />
                                : <ExpandMore />
                            )}
                        </ListItem>
                        <Collapse
                            unmountOnExit
                            in={expanded.has(streamId)}
                        >
                            <List
                                disablePadding
                                dense
                                style={{ paddingLeft: 16 }}
                            >
                                {stream.fields.filter((field) => matches(streamId) || matches(field.name)).map((field) => {
                                    const fieldId = `${streamId}.${field.name}`
                                    const labelId = `${fieldId}-label`

                                    const autoSelected = field.selected === 'AUTOMATIC'
                                    const selectedFromRules = checkFieldSelectedFromRules(field, streamId)
                                    const selectedFromStreams = checkFieldSelectedFromStreams(field, streamId)
                                    const selectedFromStreamsOnly = !selectedFromRules && selectedFromStreams

                                    return (
                                        <ListItem
                                            button
                                            key={field.name}
                                            disableTouchRipple={autoSelected}
                                            onClick={toggleFieldSelected(field, streamId)}
                                            aria-labelledby={labelId}
                                        >
                                            <ListItemIcon>
                                                <SelectableCheckbox
                                                    disabled={autoSelected}
                                                    checked={rules.size ? selectedFromRules : selectedFromStreams}
                                                    checkedIcon={<CheckBoxIcon opacity={selectedFromStreamsOnly ? 0.5 : undefined} />}
                                                    inputProps={{ 'aria-label': fieldId }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={<code id={labelId}>{field.name}</code>}
                                                secondary={autoSelected && "Automatically selected"}
                                            />
                                        </ListItem>
                                    )
                                })}
                            </List>
                        </Collapse>
                    </React.Fragment>
                )
            })}
            {!!rules.size &&
                <ListItem component="div" aria-label="Select rules" style={{ flexWrap: 'wrap', gap: 4 }}>
                    {[...rules].map((rule) => <Chip key={rule} size="small" label={<code>{rule}</code>} />)}
                </ListItem>}
        </List>
    )

}

export default StreamSelector
